import { useAuth } from "@clerk/react";
import { useCallback } from "react";

export class ApiError extends Error {
  constructor(message: string, public status: number, public fields?: unknown) {
    super(message);
  }
}

export function useApi() {
  const { getToken } = useAuth();

  return useCallback(async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const token = await getToken();
    const response = await fetch(path, {
      ...init,
      headers: {
        ...(init.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({ error: "The request could not be completed." })) as { error?: string; fields?: unknown };
      throw new ApiError(body.error ?? "The request could not be completed.", response.status, body.fields);
    }
    return response.json() as Promise<T>;
  }, [getToken]);
}

export function useDownload() {
  const { getToken } = useAuth();
  return useCallback(async (path: string, fallbackName: string) => {
    const token = await getToken();
    const response = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!response.ok) throw new ApiError("The download could not be completed.", response.status);
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const filename = disposition.match(/filename="?([^";]+)"?/)?.[1] ?? fallbackName;
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }, [getToken]);
}
